import {Avatar, Checkbox, Modal, Select} from 'antd'
import {course_createCourseEnrollment_Api} from 'apis'
import FieldDecorator from 'components/form/FieldDecorator'
import FieldsFactory from 'components/form/FieldsFactory'
import {SubmitButton} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import LookupField from 'components/LookupField'
import {SaintsOutlineSvg} from 'components/Svgs/SaintsPlaceholder'
import {Formik, FormikConsumer} from 'formik'
import {getDateString} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {BsInfoCircleFill} from 'react-icons/bs'
import {MdChurch, MdContactPhone, MdLocationOn} from 'react-icons/md'
import {RiMapPinFill} from 'react-icons/ri'
import {PiBookOpenText, PiUsersFourFill} from 'react-icons/pi'
import {IdentifierTypes} from 'views/AppLocals/appFieldTypes'
import {formSchema} from 'views/AppLocals/Course/CourseSet/CourseRegisterForm'
import {HeaderWithIconLine} from 'views/AppLocals/Templates/items/ListHeader'
import {renderIf} from 'views/Shared'
import {SelectWidget} from "../../../AppLocals/custom/ReactSelectComponents";
import {BiSolidChurch} from "react-icons/bi";

export const RELATIONSHIP_OPTIONS = [
    {value: '', label: 'no relationship (skip linking)'},
    {value: 'SELF', label: 'self (student)'},
    {value: 'FATHER', label: 'father'},
    {value: 'MOTHER', label: 'mother'},
    {value: 'BROTHER', label: 'brother / sister (sibling)'},
    {value: 'SISTER', label: 'brother / sister (sibling)'},
    {value: 'GUARDIAN', label: 'guardian'},
]

// Stable module-level arrays — prevents FieldsFactory useMemo from re-running on every render
const _studentBaseSchema = [formSchema.date_of_birth__gender]
const _identifierSchema = [formSchema.identifier]
const _contactSchema = [formSchema.contact_name, formSchema.phone__email]

const HeaderWithIcon = ({icon, title, description}) => {
    const t = useTranslate()
    return (
        <div className="flex items-start gap-2">
            <div className="mt-1 flex flex-center">
                {icon}
            </div>
            <div className="flex flex-col flex-1 gap-0.5">
                <div className="font-bold text-sm text-gray-700 tracking-wide">
                    {t(title)}
                </div>
                <div style={{ height: 3}} className="w-20 rounded-full bg-gray-400"/>
                {description && (
                    <div className="text-xs text-color-300 tracking-wider">
                        {t(description)}
                    </div>
                )}
            </div>
        </div>
    )
}

export const WalkInEnrollmentModal = ({
                                          open,
                                          courseId,
                                          onClose,
                                          onCreated,
                                          subjects = [],
                                      }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const {handleAsyncAction, isLoading} = useAsyncAction({
        apiInfo: course_createCourseEnrollment_Api,
        onSuccess: (result, meta) => {
            const newEnrollment = _.get(meta, 'response.data')
            onCreated(newEnrollment)
            onClose()
        },
    })

    const allSubjectIds = useMemo(
        () => subjects.map(s => s?.subject?.id).filter(Boolean),
        [subjects]
    )

    const initialValues = useMemo(
        () => ({
            fullname: undefined,
            gender: undefined,
            date_of_birth: undefined,
            identifier_type: IdentifierTypes.IdentityCard.value,
            identifier_code: undefined,
            patron_saint_id: undefined,
            patron_saint_name: undefined,
            patron_saint: undefined,
            contact_name: undefined,
            contact_phone: undefined,
            contact_email: undefined,
            parish_id: undefined,
            parish_name: undefined,
            parish: undefined,
            diocese_id: undefined,
            diocese_name: undefined,
            diocese: undefined,
            subject_ids: allSubjectIds,
            registrant_relationship: '',
        }),
        [allSubjectIds]
    )

    const validate = (values) => {
        const errors = {}
        if (!values.fullname) errors.fullname = t('required field')
        if (!values.date_of_birth) errors.date_of_birth = t('required field')
        if (values.gender === undefined || values.gender === null || values.gender === '')
            errors.gender = t('required field')
        if (values.identifier_code && !values.identifier_type)
            errors.identifier = t('required field')
        return errors
    }

    const handleSubmit = (values) => {
        const {
            gender,
            date_of_birth,
            subject_ids,
            patron_saint,
            parish,
            diocese,
            registrant_relationship,
            ...rest
        } = values
        handleAsyncAction(
            _.omitBy(
                {
                    ...rest,
                    date_of_birth: getDateString(date_of_birth),
                    gender: gender !== undefined && gender !== null ? Number(gender) : undefined,
                    subject_ids: subject_ids?.length ? subject_ids.join(',') : undefined,
                    is_self_reg: 0,
                    registrant_relationship: registrant_relationship || undefined,
                },
                _.isUndefined
            ),
            {id: courseId}
        )
    }

    return (
        <Modal
            className="custom-modal"
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnHidden={true}
            title={
                <div className="text-center font-bold text-color-000 tracking-wide">
                    {t('walk in enrollment')}
                </div>
            }
            width={600}
            style={{maxWidth: 'calc(100vw - 16px)'}}>
            <Formik
                initialValues={initialValues}
                validate={validate}
                validateOnMount={true}
                validateOnChange={true}
                enableReinitialize={true}
                onSubmit={handleSubmit}>
                <div className="flex flex-col gap-6">

                    {/* ── Thông tin học viên ── */}
                    <div className="flex flex-col gap-3">
                        <HeaderWithIcon
                            icon={<BsInfoCircleFill size={18} className="text-blue-400"/>}
                            title="student information"
                            className="text-sm text-color-300"
                        />
                        <div className="ml-6 flex flex-col gap-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {/* Tên thánh — dùng FormikConsumer trực tiếp để tránh vấn đề LookupField popup */}
                                <FormikConsumer>
                                    {({values, setValues}) => (
                                        <FieldDecorator label="holy name" labelProps={{className: undefined}}>
                                            <LookupField
                                                title={t('choose patron saint')}
                                                createIfNotExists={true}
                                                defaultValue={values?.patron_saint_name}
                                                selectedOption={values?.patron_saint}
                                                apiPath="/lookup/saint/get-by-types"
                                                name="patron_saint"
                                                OptionWrapper={({children, ...props}) => (
                                                    <div className="p-3 flex items-center gap-2 list-item" {...props}>
                                                        {children}
                                                    </div>
                                                )}
                                                renderOption={(o, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex items-center flex-1 gap-3">
                                                        <Avatar
                                                            size={50}
                                                            src={o.avatar}
                                                            className="flex flex-center background-200 rounded-lg"
                                                            icon={<SaintsOutlineSvg size={30} style={{color: '#dddddd'}}/>}
                                                        />
                                                        <div className="flex flex-col flex-1">
                                                    <span className="font-bold text-sm text-color-000 leading-snug">
                                                        {`${o.common_name || o.name} (${o.patron_saint_feast_day || o.feast_day})`}
                                                    </span>
                                                            {renderIf(
                                                                o?.description,
                                                                <span className="text-xs text-color-400 italic max-lines-1">
                                                            {o?.description}
                                                        </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                handleChange={(e) => {
                                                    setValues({
                                                        ...values,
                                                        patron_saint: e.target,
                                                        patron_saint_name: e.target.common_name || e.target.value,
                                                        patron_saint_feast_day: e.target.feast_day,
                                                        patron_saint_id: e.target.id,
                                                    })
                                                }}
                                            />
                                        </FieldDecorator>
                                    )}
                                </FormikConsumer>
                                <div className="col-span-1 md:col-span-2">
                                    <FieldsFactory formSchema={[formSchema.fullname]}/>
                                </div>
                            </div>

                            <FieldsFactory formSchema={_studentBaseSchema}/>

                            {/* Giấy tờ tuỳ thân — optional */}
                            <div className="p-3 flex flex-col border border-blue-300 rounded-xl">
                            <span className="font-semibold text-blue-600 uppercase tracking-wide">
                                {t('identity documents')}
                            </span>
                                <span className="mb-2 text-xs text-color-300 italic leading-snug tracking-wider">
                                {t('identifier helps verify student identity')}
                            </span>
                                <FieldsFactory formSchema={_identifierSchema}/>
                            </div>
                        </div>
                    </div>

                    {/* ── Liên hệ ── */}
                    <div className="flex flex-col gap-3">
                        <HeaderWithIcon
                            icon={<MdContactPhone size={18} className="text-green-500"/>}
                            title="student contact"
                            description="contact can be student or parent guardian"
                        />
                        <div className="ml-6 flex flex-col gap-3">
                            <FieldsFactory formSchema={_contactSchema}/>
                        </div>
                    </div>

                    {/* ── Giáo xứ / Giáo phận — optional ── */}
                    <div className="flex flex-col gap-3">
                        <HeaderWithIcon
                            icon={<BiSolidChurch size={18} className="text-secondary-500"/>}
                            title="belong to parish"
                            description="this field is optional and may be left blank if not applicable"
                        />
                        <FormikConsumer>
                            {({values, setValues}) => (
                                <div className="ml-6 grid grid-cols-2 gap-3">
                                    <FieldDecorator label="belong to parish" labelProps={{className: undefined}}>
                                        <LookupField
                                            title={t('choose parish')}
                                            createIfNotExists={true}
                                            defaultValue={values?.parish_name}
                                            selectedOption={values?.parish}
                                            placeholder={t('click to select / create')}
                                            apiPath="/lookup/parish/get-by-types"
                                            name="parish"
                                            OptionWrapper={({children, ...props}) => (
                                                <div className="p-3 flex items-center gap-2 list-item" {...props}>
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(o, i) => {
                                                const {fullname, diocese_fullname, location_address} = o ?? {}
                                                return (
                                                    <div
                                                        key={i}
                                                        className="flex items-center flex-1 gap-3">
                                                        {renderIf(
                                                            !isSm,
                                                            <Avatar
                                                                size={50}
                                                                src={o.avatar}
                                                                className="flex flex-center background-200 rounded-lg"
                                                                icon={<MdChurch size={20} style={{color: '#dddddd'}}/>}
                                                            />
                                                        )}
                                                        <div className="py-2 flex flex-col flex-1 gap-1">
                                                            <div className="space-x-1 leading-snug">
                                                                <span className="font-bold text-sm text-color-000">
                                                                    {fullname}
                                                                </span>
                                                                <span
                                                                    className="font-medium text-xs text-blue-500 italic leading-snug">
                                                                    {`( ${diocese_fullname} )`}
                                                                </span>
                                                            </div>
                                                            {renderIf(
                                                                location_address,
                                                                <div className="flex items-center gap-x-1">
                                                                    <RiMapPinFill size={11} className="text-color-400"/>
                                                                    <span className="flex-1 max-lines-1 text-xs text-color-400 italic leading-snug">
                                                                        {location_address}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                            handleChange={(e) => {
                                                setValues({
                                                    ...values,
                                                    parish: e.target,
                                                    parish_name: e.target.value,
                                                    parish_id: e.target.id,
                                                    diocese: e.target.diocese,
                                                    diocese_name: e.target?.diocese?.name,
                                                    diocese_id: e.target?.diocese?.id,
                                                })
                                            }}
                                        />
                                    </FieldDecorator>
                                    <FieldDecorator label="diocese" labelProps={{className: undefined}}>
                                        <LookupField
                                            title={t('choose diocese')}
                                            createIfNotExists={true}
                                            defaultValue={values?.diocese_name}
                                            selectedOption={values?.diocese}
                                            apiPath="/lookup/diocese/get-by-types"
                                            name="diocese"
                                            OptionWrapper={({children, ...props}) => (
                                                <div className="p-3 flex items-center gap-2 list-item" {...props}>
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(o, i) => {
                                                const {fullname, location_address} = o ?? {}
                                                return (
                                                    <div
                                                        key={i}
                                                        className="flex items-center flex-1 gap-3">
                                                        <Avatar
                                                            size={50}
                                                            src={o.avatar}
                                                            className="flex flex-center background-200 rounded-lg"
                                                            icon={<MdChurch size={20} style={{color: '#dddddd'}}/>}
                                                        />
                                                        <div className="py-2 flex flex-col flex-1 gap-1">
                                                            <div className="leading-snug">
                                                                <span className="font-bold text-sm text-color-000">
                                                                    {fullname}
                                                                </span>
                                                            </div>
                                                            {renderIf(
                                                                location_address,
                                                                <div className="flex items-center gap-x-1">
                                                                    <RiMapPinFill size={11} className="text-color-400"/>
                                                                    <span className="flex-1 max-lines-1 text-xs text-color-400 italic leading-snug">
                                                                        {location_address}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                            handleChange={(e) => {
                                                setValues({
                                                    ...values,
                                                    diocese: e.target,
                                                    diocese_name: e.target?.value,
                                                    diocese_id: e.target?.id,
                                                })
                                            }}
                                        />
                                    </FieldDecorator>
                                </div>
                            )}
                        </FormikConsumer>
                    </div>

                    {/* ── Môn học — default chọn all ── */}
                    {subjects.length > 0 && (
                        <FormikConsumer>
                            {({values, handleChange}) => (
                                <div className="flex flex-col gap-2">
                                    <HeaderWithIcon
                                        icon={<PiBookOpenText size={18} className="text-color-300"/>}
                                        title="choose subject"
                                        description="default all subjects selected"
                                    />
                                    <FieldDecorator>
                                        <Checkbox.Group
                                            className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3"
                                            name="subject_ids"
                                            value={values.subject_ids}
                                            options={subjects.map(s => ({
                                                value: s?.subject?.id,
                                                label: s?.subject?.title,
                                            }))}
                                            onChange={val =>
                                                handleChange(createValue('subject_ids', val))
                                            }
                                        />
                                    </FieldDecorator>
                                </div>
                            )}
                        </FormikConsumer>
                    )}

                    {/* ── Mối quan hệ với học viên ── */}
                    {/*<FormikConsumer>*/}
                    {/*    {({values, setFieldValue}) => (*/}
                    {/*        <div className="flex flex-col gap-3">*/}
                    {/*            <HeaderWithIcon*/}
                    {/*                icon={<PiUsersFourFill size={18} className="text-primary-500"/>}*/}
                    {/*                title="registrant relationship to student"*/}
                    {/*                description="leave blank to skip account linking"*/}
                    {/*            />*/}
                    {/*            <div className="ml-6">*/}
                    {/*                <SelectWidget*/}
                    {/*                    allowClear*/}
                    {/*                    value={values.registrant_relationship || undefined}*/}
                    {/*                    placeholder={t('no relationship (skip linking)')}*/}
                    {/*                    onChange={val => setFieldValue('registrant_relationship', val || '')}*/}
                    {/*                    className="w-full"*/}
                    {/*                    options={RELATIONSHIP_OPTIONS.slice(1).map(o => ({*/}
                    {/*                        value: o.value,*/}
                    {/*                        label: t(o.label),*/}
                    {/*                    }))}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*</FormikConsumer>*/}

                    <div className="pt-4 border-t border-color-50">
                        <SubmitButton
                            label="walk in enrollment"
                            dirtyRequired={false}
                            loading={isLoading}
                            className="w-full rounded-xl font-semibold"
                            style={{height: 48}}
                        />
                    </div>
                </div>
            </Formik>
        </Modal>
    )
}
